<template>
  <div class="wrap vertical-spacing">
    <Loader :loading="isCreateCartLoading" :text="$t('Loading Cart...')">
      <form @submit.prevent="printOffer" class="container-fluid">
        <div class="row bottom-xs">
          <div class="col-md-6 col-xs-12 m-b-30">
            <SfInput :label="$t('Name')" v-model="name" name="name" :required="true" />
            <SfInput v-model="comment" :label="$t('Comment')" name="comment" />
          </div>
          <div class="cart col-xs-12">
            <Loader :loading="isCartLoading" :text="$t('Updating Cart...')">
              <SfTable v-if="cart.length" class="m-b-30">
                <SfTableHeading>
                  <SfTableHeader v-for="header in tableHeaders" :key="header">
                    {{ $t(header) }}
                  </SfTableHeader>
                </SfTableHeading>
                <SfTableRow v-for="(row, rowIndex) in tableRows" :key="row[0]">
                  <SfTableData
                    class="cell"
                    v-for="(data, index) in row"
                    :key="`${tableHeaders[index]}${offer[rowIndex].sku}-${data}`"
                  >
                    <div class="discount" v-if="tableHeaders[index] === 'Discount %'">
                      <SfInput
                        class="discount-input disable-print"
                        :value="discounts[rowIndex]"
                        @input="setDiscount($event, rowIndex)"
                        @blur="updateRows(rowIndex)"
                        type="number"
                        name="discount"
                        min="0"
                        max="100"
                        step=".01"
                      />
                      <div class="disable-print">
                        {{ $t('Your Discount') }}: {{ cart[rowIndex].Product.Discount }}%
                      </div>
                      <span class="enable-print">{{ data }}</span>
                      <span
                        v-if="discounts[rowIndex] > cart[rowIndex].Product.Discount"
                        class="text-danger disable-print"
                      >
                        {{ $t('The customer discount is greater than yours.') }}
                      </span>
                    </div>
                    <span v-else>
                      {{ data }}
                    </span>
                  </SfTableData>
                </SfTableRow>
              </SfTable>
              <div v-else>
                <SfHeading :title="$t('Your shopping cart is empty.')" :level="2" />
              </div>
            </Loader>
          </div>
          <div
            v-if="showTotals"
            class="disable-print total-items col-md-offset-4 col-xs-12 col-md-4"
          >
            <SfHeading class="sf-heading--left m-b-20" :title="$t('Your Summary')" :level="3" />
            <Totals class="m-b-20" />
          </div>
          <div v-if="showTotals" class="total-items col-xs-12 col-md-4">
            <Loader :loading="isCartLoading" :text="$t('Updating Cart...')">
              <div class="totals-group">
                <div class="discount-cart-wrapper">
                  <SfInput
                    :label="`${$t('Order Total Discount')} (zł)`"
                    class="discount-cart"
                    :value="orderTotalDiscount"
                    @input="setCartDiscount"
                    @blur="updateTotals"
                    type="number"
                    step=".01"
                    name="cartDiscount"
                  />
                </div>
                <SfHeading
                  class="disable-print sf-heading--left m-b-20"
                  :title="$t('Client Summary')"
                  :level="3"
                />
                <Totals :customTotals="customTotals" class="m-b-20" />
              </div>
            </Loader>
          </div>
          <div v-if="showTotals" class="total-items col-md-offset-8 col-xs-12 col-md-4">
            <span
              v-if="customTotals.OrderTotal < totals.OrderTotal"
              class="text-danger disable-print"
            >
              {{ $t('Your order is more expensive than your customer\'s.') }}
            </span>
            <SfButton type="submit" class="m-t-10 sf-button--full-width color-primary">
              {{ $t('Print Offer') }}
            </SfButton>
            <SfButton
              @click="$router.push('/cart')"
              type="button"
              class="m-tb-10 sf-button--outline sf-button--full-width"
            >
              {{ $t('Return to cart') }}
            </SfButton>
          </div>
        </div>
      </form>
    </Loader>
  </div>
</template>

<script>
import {
  SfHeading, SfTable, SfButton, SfInput,
} from '@storefront-ui/vue';
import { Loader, config } from '@/modules/core';
import { user } from '@/modules/user';
import currency from 'currency.js';

import cart from '../mixins/cart';
import Totals from '../components/Totals.vue';

export default {
  name: 'Cart',
  mixins: [cart, user, config],
  components: {
    SfHeading,
    SfTable,
    SfInput,
    SfButton,
    Loader,
    Totals,
  },
  created() {
    this.fetchCart()
      .then((offer) => {
        this.offer = this.mapOfferData(offer);
        this.discounts = offer.map((item) => item.Product.Discount);
        return this.fetchCurrentCustomer();
      })
      .then(() => this.fetchTotals({
        ShippingAddressId: this.customer.Addresses[0]?.Id || '',
        BillingAddressId: this.customer.Addresses[0]?.Id || '',
      }))
      .then((totals) => {
        this.customTotals = totals;
        this.orderTotalDiscount = totals.OrderTotalDiscount || null;
      })
      .finally(() => {
        this.isCreateCartLoading = false;
      });
  },
  data() {
    return {
      name: '',
      comment: '',
      isCreateCartLoading: true,
      offer: [],
      orderTotalDiscount: null,
      discounts: [],
      customTotals: {},
      tableHeaders: ['SKU', 'Name', 'Qty', 'Gross Catalog Price', 'Gross Price', 'Sub Total', 'Discount %'],
    };
  },
  computed: {
    tableRows() {
      return this.offer.map((item) => [
        item.sku,
        item.name,
        item.qty,
        this.currencyFormatter().format(item.catalogPrice),
        this.currencyFormatter().format(item.price),
        this.currencyFormatter().format(item.total),
        item.discount || 0,
      ]);
    },
  },
  methods: {
    printOffer() {
      const {
        offer, orderTotalDiscount, name, comment,
      } = this;
      const payload = {
        offer,
        orderTotalDiscount,
        name,
        comment,
      };
      console.log(payload);
      window.print();
    },
    fixFormat(value, min, max) {
      let newValue;
      if (value > max) {
        newValue = max;
      } else if (value < min) {
        newValue = min;
      } else {
        newValue = value;
      }

      return newValue;
    },
    setDiscount(discount, index) {
      const newDiscount = this.fixFormat(discount, 0, 100);
      this.discounts.splice(index, 1, newDiscount);
    },
    setCartDiscount(discount) {
      // eslint-disable-next-line max-len
      this.orderTotalDiscount = this.fixFormat(-Math.abs(discount), -Math.abs(this.customTotals.Total), 0) || null;
    },
    calcDiscount(price, discount) {
      const subtract = currency(price).multiply(discount / 100);
      return currency(price).subtract(subtract);
    },
    updateRows(index) {
      const discount = this.discounts[index];
      const { catalogPrice, qty } = this.offer[index];
      const price = this.calcDiscount(catalogPrice, discount);
      this.offer.splice(index, 1, {
        ...this.offer[index],
        discount,
        price,
        total: currency(price).multiply(qty),
      });

      this.updateTotals();
    },
    updateTotals() {
      const totalsCalculation = this.offer.reduce(
        (prev, curr) => {
          const catalogTotalPrice = currency(curr.catalogPrice).multiply(curr.qty);
          const SubTotal = currency(prev.SubTotal).add(catalogTotalPrice);
          const OrderTotal = currency(prev.OrderTotal).add(curr.total);

          return {
            ...prev,
            SubTotal,
            OrderTotal,
          };
        },
        {
          SubTotal: 0,
          OrderTotal: 0,
        },
      );

      const { SubTotal, OrderTotal } = totalsCalculation;
      const orderTotalDiscount = this.orderTotalDiscount ? currency(this.orderTotalDiscount) : null;

      this.customTotals = {
        ...this.customTotals,
        ...totalsCalculation,
        SubTotalDiscount: currency(SubTotal).subtract(OrderTotal),
        OrderTotalDiscount: orderTotalDiscount?.value || null,
        OrderTotal: this.orderTotalDiscount
          ? currency(OrderTotal).add(this.orderTotalDiscount)
          : OrderTotal,
      };
    },
    mapOfferData(data) {
      return data.map((item) => ({
        sku: item.Product.Sku,
        name: item.Product.Name,
        qty: item.Quantity,
        catalogPrice: item.Product.OldPrice || item.Product.Price,
        price: item.Product.Price,
        total: item.Total,
        discount: item.Product.Discount,
        originalDiscount: item.Product.Discount,
      }));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@storefront-ui/shared/styles/_helpers';
@import 'src/scss/_breakpoint.scss';

.discount-cart {
  float: right;
  max-width: 200px;
}

.discount-cart-wrapper {
  overflow: hidden;
}

.discount-input {
  min-width: 60px;
  display: inline-block;
}

.discount-sign {
  display: inline-block;
}

.fz-sidebar__cart-items {
  margin: var(--spacer-sm) 0;
  & > span {
    font-weight: var(--font-medium);
  }
}

@include flexbox-mobile {
  .cart {
    margin-bottom: var(--spacer-sm);
  }
}

.sf-collected-product {
  --collected-product-width: 100%;
}

.cell {
  vertical-align: middle;
  align-items: center;
  display: flex;
}

.enable-print {
  display: none;
}

@media print {
  @page {
    size: A4;
  }

  table {
    page-break-inside: avoid;
  }

  button,
  .disable-print,
  .discount-cart {
    display: none;
  }

  .enable-print {
    display: block;
  }
}
</style>
